import React, { useEffect, useRef } from 'react';

const CHECKOUT_URL = 'https://whop.com/checkout/plan_3VIsMeJcCRHpX'; /* <!-- ADD CHECKOUT URL --> */

/* ─── Scroll reveal hook ─── */
const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.classList.add('nry-in'); io.unobserve(el); } },
      { threshold: 0.08 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return ref;
};

const Reveal = ({ children, className = '', delay = 0 }) => {
  const ref = useReveal();
  return (
    <div ref={ref} className={`nry-fade ${className}`} style={delay ? { transitionDelay: `${delay}ms` } : undefined}>
      {children}
    </div>
  );
};

/* ─── Gold CTA Button ─── */
const GoldCTA = ({ children, href, onClick, className = '', large = false, full = false, testId }) => {
  const cls = `g-btn ${full ? 'w-full' : ''} ${large ? '' : ''} ${className}`;
  if (href) return <a href={href} target="_blank" rel="noopener noreferrer" onClick={onClick} data-testid={testId} className={cls} style={large ? { padding:'16px 40px' } : {}}>{children}</a>;
  return <button onClick={onClick} data-testid={testId} className={cls} style={large ? { padding:'16px 40px' } : {}}>{children}</button>;
};

/* ─── Section label ─── */
const Label = ({ children }) => (
  <span className="g-badge">{children}</span>
);

/* ─── Feature icon ─── */
const FeatureIcon = ({ d }) => (
  <div className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(201,168,76,0.12)' }}>
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={d} /></svg>
  </div>
);

/* ─── Green check ─── */
const GreenCheck = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="flex-shrink-0 mt-0.5">
    <circle cx="12" cy="12" r="10" fill="rgba(0,217,126,0.15)" />
    <path d="M8 12l2.5 2.5L16 9.5" stroke="#00D97E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const NotReadyYet = () => {
  useEffect(() => {
    document.title = 'OFS Foundations | Order Flow School';
    window.scrollTo(0, 0);
  }, []);

  const scrollTo = (e, id) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const features = [
    { icon: 'M4 19.5A2.5 2.5 0 016.5 17H20M4 19.5V4.5A2.5 2.5 0 016.5 2H20v20H6.5A2.5 2.5 0 014 19.5z', title: '300+ Page Order Flow Guide', desc: 'A complete breakdown of how markets actually move — from liquidity pools to institutional execution. This replaces every YouTube rabbit hole.' },
    { icon: 'M15 10l-4 4 6 6M4 4l6 6', title: 'Live Trade Commentary Sessions', desc: 'Watch real trades set up and executed step by step. You learn the reasoning, not just the result.' },
    { icon: 'M12 20V10M18 20V4M6 20v-4', title: 'Real Execution Breakdowns', desc: 'Clear examples of how trades are planned, triggered, and exited in real time. See exactly what confirmation looks like.' },
    { icon: 'M12 8v4l3 3M21 12a9 9 0 11-18 0 9 9 0 0118 0z', title: '4+ Hours of Structured Training', desc: 'Focused lessons on footprint charts, liquidity levels, and confirmation signals — sequenced to build understanding, not overwhelm.' },
    { icon: 'M15 12a3 3 0 11-6 0 3 3 0 016 0zM2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z', title: 'Foundation to Stop Trading Blind', desc: "Understand participation, timing, and the true drivers of price movement. Once you see this, you can't unsee it." },
    { icon: 'M13 7l5 5m0 0l-5 5m5-5H6', title: 'The Path Forward', desc: 'This is the entry point to the full Order Flow School ecosystem. Traders who build a real edge here are the ones who unlock what comes next.', featured: true },
  ];

  const painCards = [
    { emoji: '\uD83D\uDCC9', title: "Trading signals you don't understand", desc: "You take entries because someone said so — not because you can read what the market is doing." },
    { emoji: '\uD83D\uDD01', title: 'Inconsistent results every week', desc: "Some weeks up, most down. You can't tell if it's the setup or your execution." },
    { emoji: '\uD83E\uDDF1', title: 'No structure for reading the market', desc: "Footprint, liquidity, order flow — it all sounds like noise until someone shows you the actual framework." },
    { emoji: '\uD83D\uDE24', title: 'Emotional decisions at the worst times', desc: "FOMO, revenge trading, holding losers. Without a real process, psychology alone won't save you." },
  ];

  const forYou = [
    "You're serious about trading — not looking for a shortcut",
    "You're willing to study structure, not just copy entries",
    "You've tried other approaches and know something's missing",
    "You can commit real time to learning, not just watching",
    "You want a framework that makes the market make sense",
  ];

  const notForYou = [
    "You want signals handed to you without understanding why",
    "You're looking for a get-rich-quick guarantee",
    "You expect results in your first week",
    "You're not willing to put in the reps",
    "You're here to watch videos and do nothing with them",
  ];

  const checklist = [
    '300+ page order flow guide',
    '4+ hours of structured video training',
    'Live trade commentary sessions',
    'Real execution breakdowns',
    'Foundation to understand why price moves',
    'Path to unlock what comes next',
  ];

  return (
    <>
      <style>{`
        @keyframes ofs-pulse-k { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(1.5)} }
        .ofs-pulse-dot { width:8px; height:8px; border-radius:50%; background:#00D97E; animation: ofs-pulse-k 1.5s infinite; }
        .nry-fade { opacity:0; transform:translateY(20px); transition: opacity 0.6s ease, transform 0.6s ease; }
        .nry-in { opacity:1; transform:translateY(0); }
        .ofs-sticky-wrap { position:fixed; top:0; left:0; right:0; z-index:999; border-bottom:1px solid rgba(201,168,76,0.15); backdrop-filter:blur(12px); -webkit-backdrop-filter:blur(12px); transition: transform 0.4s cubic-bezier(0.34,1.56,0.64,1); }
        .ofs-sticky-wrap.out { transform:translateY(-100%); }
        .ofs-sticky-wrap.in { transform:translateY(0); }
        @media(max-width:480px) { .ofs-sticky-hide { display:none !important; } .ofs-sticky-btn-m { width:100%; justify-content:center; padding:12px 20px !important; } }
      `}</style>

      <div className="min-h-screen" style={{ background: 'var(--bg)', color: 'var(--text)', fontFamily: "'Inter', sans-serif", paddingTop: 64 }} data-testid="foundations-page">

        {/* ══════════════ HERO ══════════════ */}
        <section className="pt-28 pb-20 md:pt-36 md:pb-28" data-testid="foundations-hero">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="space-y-8">
              {/* Green pill */}
              <div className="flex justify-center">
                <span className="ofs-badge" style={{ background: 'rgba(0,217,126,0.08)', borderColor: 'rgba(0,217,126,0.3)', color: '#00D97E' }}>
                  <span className="ofs-pulse-dot" style={{ marginRight: 8 }} />
                  Application Review Complete — You're In
                </span>
              </div>

              {/* Headline */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.2] tracking-tight" data-testid="hero-headline"
                style={{ fontFamily: "'League Spartan', sans-serif", letterSpacing: '-0.01em' }}>
                You've been approved for{' '}
                <span style={{ color: '#C9A84C' }}>OFS Foundations</span>.
              </h1>

              {/* Subheadline */}
              <p className="text-lg md:text-xl leading-relaxed max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
                This is where serious traders start. You now have access to the exact foundation every profitable OFS trader built first.
              </p>

              {/* Reframe block */}
              <div className="max-w-xl mx-auto text-left rounded-xl p-5"
                style={{ background: 'var(--bg-card)', borderLeft: '3px solid #C9A84C', border: '1px solid rgba(255,255,255,0.07)', borderLeftWidth: 3, borderLeftColor: '#C9A84C' }}>
                <p className="text-sm leading-relaxed" style={{ color: 'rgba(226,234,242,0.65)' }}>
                  Hundreds of traders inside Order Flow School started here. This is the material they used before everything clicked.
                </p>
              </div>

              {/* CTA */}
              <div className="space-y-3">
                <GoldCTA href={CHECKOUT_URL} large testId="hero-cta-button">
                  Get Instant Access <span className="text-xl">&rarr;</span>
                </GoldCTA>
                <p className="text-sm" style={{ color: 'rgba(226,234,242,0.35)' }}>
                  One payment &middot; Lifetime access &middot; 30-day money-back guarantee
                </p>
              </div>
            </div>
          </div>

          {/* Subtle glow */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden" style={{ top: 0 }}>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] rounded-full"
              style={{ background: 'radial-gradient(ellipse, rgba(201,168,76,0.05) 0%, transparent 70%)' }} />
          </div>
        </section>

        <hr className="border-0 h-px" style={{ background: 'rgba(255,255,255,0.07)' }} />

        {/* ══════════════ FEATURES ══════════════ */}
        <section className="py-16 md:py-24">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="space-y-4 mb-12">
                <Label>What's Inside</Label>
                <h2 className="text-3xl md:text-4xl font-bold leading-tight" style={{ fontFamily:"'League Spartan',sans-serif", letterSpacing:'-0.01em' }}>
                  Everything you need to stop guessing and start reading the market.
                </h2>
              </div>
            </Reveal>

            <div className="grid md:grid-cols-2 gap-6">
              {features.map((f, i) => (
                <Reveal key={i} delay={i * 80}>
                  <div className={`rounded-xl p-6 space-y-4 transition-all duration-300 hover:border-opacity-30 ${f.featured ? '' : ''}`}
                    style={{
                      background: f.featured ? 'rgba(201,168,76,0.04)' : '#0A0A0A',
                      border: f.featured ? '1px solid rgba(201,168,76,0.2)' : '1px solid rgba(255,255,255,0.07)',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(201,168,76,0.3)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = f.featured ? 'rgba(201,168,76,0.2)' : 'rgba(255,255,255,0.07)'; }}
                  >
                    <FeatureIcon d={f.icon} />
                    <h3 className="text-lg font-bold text-white">{f.title}</h3>
                    <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{f.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <hr className="border-0 h-px" style={{ background: 'rgba(255,255,255,0.07)' }} />

        {/* ══════════════ VSL (hidden) ══════════════ */}
        {/* <!-- VSL SECTION — REMOVE display:none WHEN VIDEO IS READY --> */}
        <div style={{ display: 'none' }} data-testid="vsl-section">
          <section className="py-16 md:py-24">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <Label>Watch This First</Label>
              <div className="relative rounded-xl overflow-hidden mt-6" style={{ paddingTop: '56.25%', background: 'var(--bg-card)', border: '1px solid rgba(255,255,255,0.07)' }}>
                {/* <!-- REPLACE THIS DIV WITH YOUR VIMEO/WISTIA EMBED --> */}
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ border: '2px solid #C9A84C' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="#C9A84C"><polygon points="6,3 20,12 6,21" /></svg>
                  </div>
                  <p className="text-lg font-bold text-white">Watch This Before You Start</p>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>A message from the founder &middot; ~4 min</p>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* THE REAL PROBLEM SECTION — hidden per owner request 2026-05-31, do not delete */}
        {/* ══════════════ PAIN ══════════════ */}
        <section className="py-16 md:py-24" style={{ display: 'none' }}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="space-y-4 mb-12">
                <Label>The Real Problem</Label>
                <h2 className="text-3xl md:text-4xl font-bold leading-tight" style={{ fontFamily:"'League Spartan',sans-serif", letterSpacing:'-0.01em' }}>
                  Most traders fail for one reason. They skip the foundation.
                </h2>
              </div>
            </Reveal>

            <div className="grid md:grid-cols-2 gap-6">
              {painCards.map((p, i) => (
                <Reveal key={i} delay={i * 80}>
                  <div className="rounded-xl p-6 space-y-3" style={{ background: 'var(--bg-card)', border: '1px solid rgba(255,255,255,0.07)' }}>
                    <span className="text-2xl block">{p.emoji}</span>
                    <h3 className="text-lg font-bold text-white">{p.title}</h3>
                    <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{p.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* hr below PAIN — hidden because PAIN is hidden, prevents doubled divider */}

        {/* ══════════════ STATS BAR ══════════════ */}
        <Reveal>
          <section className="py-12 md:py-16" style={{ background: 'var(--bg-card)' }}>
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                {[
                  { num: '300+', label: 'Page order flow guide' },
                  { num: '4+ hrs', label: 'Structured video training' },
                  { num: '$100', label: 'One-time, lifetime access' },
                  { num: '30 days', label: 'Full money-back guarantee' },
                ].map((s, i) => (
                  <div key={i}>
                    <p className="text-3xl md:text-4xl font-extrabold" style={{ color: 'var(--gold)', fontFamily:"'League Spartan',sans-serif" }}>{s.num}</p>
                    <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </Reveal>

        <hr className="border-0 h-px" style={{ background: 'rgba(255,255,255,0.07)' }} />

        {/* STUDENT RESULTS SECTION — hidden per owner request 2026-05-31, do not delete */}
        {/* ══════════════ SOCIAL PROOF ══════════════ */}
        <section className="py-16 md:py-24" style={{ display: 'none' }}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="text-center space-y-4 mb-12">
                <Label>Student Results</Label>
                <h2 className="text-3xl md:text-4xl font-bold leading-tight" style={{ fontFamily:"'League Spartan',sans-serif", letterSpacing:'-0.01em' }}>
                  Traders who built this foundation — then got paid.
                </h2>
                <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>
                  These results came after completing the same material you now have access to.
                </p>
              </div>
            </Reveal>

            <div className="grid md:grid-cols-2 gap-6">
              {[
                { url: 'https://player.vimeo.com/video/1175926546?badge=0&autopause=0&player_id=0', amount: '$10,000+', label: 'First funded account withdrawal' },
                { url: 'https://player.vimeo.com/video/1175926594?badge=0&autopause=0&player_id=0', amount: '$20,000+', label: 'Passed $200K eval, 3rd attempt' },
                { url: 'https://player.vimeo.com/video/1175926642?badge=0&autopause=0&player_id=0', amount: '$15,000+', label: 'Consistent payouts over 60 days' },
                { url: 'https://player.vimeo.com/video/1175926825?badge=0&autopause=0&player_id=0', amount: '$35,000+', label: 'Multi-account funded trader' },
              ].map((vid, i) => (
                <Reveal key={i} delay={i * 100}>
                  <div className="rounded-xl overflow-hidden" style={{ background: 'var(--bg-card)', border: '1px solid rgba(255,255,255,0.07)' }}>
                    <iframe
                      src={vid.url}
                      className="w-full aspect-video"
                      allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                      referrerPolicy="origin"
                      loading="lazy"
                      allowFullScreen
                      title={`${vid.amount} payout`}
                      data-testid={`result-video-${i}`}
                    />
                    <div className="px-5 py-4">
                      <p className="text-lg font-bold" style={{ color: '#00D97E' }}>{vid.amount}</p>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>{vid.label}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>

            <p className="text-center text-xs mt-10" style={{ color: 'rgba(226,234,242,0.3)' }}>
              Results are not typical and depend on individual effort, consistency, and market conditions.
            </p>
          </div>
        </section>

        {/* hr below SOCIAL PROOF — hidden because SOCIAL PROOF is hidden, prevents doubled divider */}

        {/* ══════════════ FOR WHO / NOT FOR WHO ══════════════ */}
        <section className="py-16 md:py-24">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <h2 className="text-3xl md:text-4xl font-bold leading-tight text-center mb-12">
                This is for the right trader. Not every trader.
              </h2>
            </Reveal>

            <div className="grid md:grid-cols-2 gap-8">
              <Reveal delay={0}>
                <div className="rounded-xl p-6 space-y-5" style={{ background: 'var(--bg-card)', border: '1px solid rgba(0,217,126,0.15)' }}>
                  <p className="text-xs font-bold uppercase tracking-[0.1em]" style={{ color: '#00D97E' }}>
                    This is for you if...
                  </p>
                  <ul className="space-y-4">
                    {forYou.map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className="font-bold text-base mt-0.5" style={{ color: '#00D97E' }}>&#10003;</span>
                        <span className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>

              <Reveal delay={100}>
                <div className="rounded-xl p-6 space-y-5" style={{ background: 'var(--bg-card)', border: '1px solid rgba(255,45,85,0.15)' }}>
                  <p className="text-xs font-bold uppercase tracking-[0.1em]" style={{ color: '#FF2D55' }}>
                    This is NOT for you if...
                  </p>
                  <ul className="space-y-4">
                    {notForYou.map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className="font-bold text-base mt-0.5" style={{ color: '#FF2D55' }}>&#10007;</span>
                        <span className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        <hr className="border-0 h-px" style={{ background: 'rgba(255,255,255,0.07)' }} />

        {/* ══════════════ PRICING / CTA ══════════════ */}
        <section className="py-16 md:py-24" id="get-access">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="text-center space-y-4 mb-12">
                <Label>Get Instant Access</Label>
                <h2 className="text-3xl md:text-4xl font-bold leading-tight" style={{ fontFamily:"'League Spartan',sans-serif", letterSpacing:'-0.01em' }}>
                  Start building the foundation that changes everything.
                </h2>
                <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>
                  One payment. Lifetime access. No monthly fees.
                </p>
              </div>
            </Reveal>

            <Reveal>
              <p className="max-w-xl mx-auto text-center text-sm italic mb-6" style={{ color: 'rgba(226,234,242,0.45)' }}>
                Price increases as new content is added. Lock in lifetime access at $100 today.
              </p>
              <div className="max-w-xl mx-auto rounded-2xl overflow-hidden" style={{ background: 'var(--bg-card)', border: '1px solid rgba(201,168,76,0.3)' }} data-testid="pricing-card">
                {/* Gold top line */}
                <div className="h-0.5" style={{ background: 'linear-gradient(90deg, #C9A84C, #E0BD6A, #C9A84C)' }} />

                <div className="p-8 md:p-10 text-center space-y-8">
                  <div className="space-y-2">
                    <p className="text-xs uppercase tracking-[0.14em]" style={{ color: 'var(--text-secondary)' }}>
                      Order Flow School — Foundations
                    </p>
                    <p className="text-5xl md:text-6xl font-extrabold" style={{ color: 'var(--gold)', fontFamily:"'League Spartan',sans-serif" }}>$100</p>
                    <p className="text-sm" style={{ color: 'rgba(226,234,242,0.35)' }}>one-time</p>
                  </div>

                  <div className="space-y-4 text-left">
                    {checklist.map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <GreenCheck />
                        <span className="text-sm leading-relaxed" style={{ color: 'rgba(226,234,242,0.7)' }}>{item}</span>
                      </div>
                    ))}
                  </div>

                  {/* <!-- ADD CHECKOUT URL --> */}
                  <GoldCTA href={CHECKOUT_URL} large full testId="get-access-button">
                    Get Instant Access <span className="text-xl">&rarr;</span>
                  </GoldCTA>

                  <div className="flex items-center justify-center gap-2">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#00D97E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
                    <p className="text-xs leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                      30-day money-back guarantee. If you don't find value — full refund. No questions.
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>

            {/* Gold note */}
            <Reveal>
              <div className="max-w-xl mx-auto mt-6 rounded-xl p-5" style={{ background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.15)' }}>
                <p className="text-sm leading-relaxed" style={{ color: 'rgba(226,234,242,0.55)' }}>
                  <strong style={{ color: '#C9A84C' }}>What comes next?</strong> Once you've built real understanding of order flow and developed your edge, you'll be positioned to unlock the next level of the Order Flow School ecosystem. That's when things get serious.
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        <hr className="border-0 h-px" style={{ background: 'rgba(255,255,255,0.07)' }} />

        {/* ══════════════ FOOTER ══════════════ */}
        <footer className="g-footer" data-testid="foundations-footer">
          <div className="max-w-6xl mx-auto text-center space-y-3">
            <p style={{ fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:28, color:'var(--gold)' }}>OFS</p>
            <p className="text-xs" style={{ color: 'var(--text-dim)' }}>&copy; 2025 Order Flow School. All rights reserved.</p>
            <p className="text-xs" style={{ color: 'var(--red)', fontStyle:'italic' }}>Results shown are not typical. Trading involves substantial risk of loss.</p>
          </div>
        </footer>

        {/* ══════════════ STICKY BAR (top of viewport) ══════════════ */}
        <div className="ofs-sticky-wrap in" style={{ background: 'var(--gold)' }} data-testid="sticky-cta-bar">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
            <div className="ofs-sticky-hide">
              <p className="text-sm font-medium" style={{ color: 'var(--btn-text)', fontFamily:"'Inter',sans-serif", fontWeight:700 }}>Order Flow School — Foundations</p>
              <p className="text-xs" style={{ color: 'rgba(26,15,0,0.6)' }}>One payment &middot; Lifetime access</p>
            </div>
            <a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" className="ofs-sticky-btn-m flex-shrink-0" style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: 'var(--bg)', color: 'var(--gold)',
              fontFamily: "'Inter',sans-serif", fontWeight: 800, fontSize: 14,
              letterSpacing: '0.16em', textTransform: 'uppercase',
              borderRadius: 9999, padding: '12px 24px',
              textDecoration: 'none', transition: 'all 0.2s ease',
            }}>
              Get OFS Foundations — $100 <span>&rarr;</span>
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default NotReadyYet;
